import { Injectable, computed, signal } from "@angular/core";
import { JobTimeTrackingService } from "../job-time-tracking-modal/job-time-tracking.service";
import { HotToastService } from "@ngneat/hot-toast";
import { StopTimerConfirmationModalComponent } from "../stop-timer-confirmation-modal/stop-timer-confirmation-modal.component";
import { DashboardService } from "src/app/dashboard/dashboard.service";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { Subscription } from "rxjs";
import { HttpResponse } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class TimerService {
  job: any;
  start: boolean = false;
  timer: any;
  currentTimeTracked: string;
  startedWithPreviousTimeTracked = { res: false, previousValue: 0 };
  isPaused: boolean = false;
  currentValue: number;
  secondsSignal = signal(0);
  startWithPreviousValueSubscription: Subscription;
  isLoading: boolean = false;

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private dashboardService: DashboardService,
    public localStorageService: LocalStorageService,
    private toastr: HotToastService,
    public jobTimeTrackingService: JobTimeTrackingService,
  ) {}

  formattedTime = computed(() => {
    const value = this.jobTimeTrackingService.timeTrackedFormatter(
      this.secondsSignal(),
    );
    return value;
  });

  openStopConfirmationModal(job_name): void {
    this.bsModalRef = this.modalService.show(
      StopTimerConfirmationModalComponent,
      {
        initialState: { jobName: job_name },
      },
    );
    this.bsModalRef.content.onClose.subscribe(async (confirmed) => {
      if (confirmed) {
        await this.timerStop();
        this.toastr.success(`Time tracking stopped for job ${this.job.name}`);
      }
    });
  }

  startLocalTimer(baseSeconds = 0): void {
    const start = new Date();
    const initial = baseSeconds;
    this.start = true;

    this.timer = setInterval(() => {
      const current = new Date();

      const startSeconds = Math.floor(+start / 1000);
      const nowSeconds = Math.floor(+current / 1000);

      const count = nowSeconds - startSeconds;
      this.secondsSignal.set(initial + count);
    }, 1000);
  }

  async timerStop() {
    clearInterval(this.timer);
    const seconds = this.secondsSignal();
    const time_tracked = { time_tracked: seconds };
    this.start = false;
    this.secondsSignal.set(0);
    this.currentTimeTracked =
      this.jobTimeTrackingService.timeTrackedFormatter(seconds);

    this.isLoading = true;
    try {
      await this.jobTimeTrackingService.stopTimer(this.job.uuid, time_tracked);
    } catch (error) {
      this.toastr.error("Error stopping time tracking");
      console.error(error);
    } finally {
      this.isLoading = false;
    }

    this.jobTimeTrackingService.isTracking.next(false);
    this.dashboardService.startTimeTracking.next(true);
  }

  async timerPause(): Promise<void> {
    clearInterval(this.timer);
    this.start = false;

    this.isLoading = true;
    try {
      const response =
        await this.jobTimeTrackingService.pauseResumeTimer("pause");
      if (response) {
        this.isPaused = true;
      } else {
        this.toastr.error("Error pausing the timer");
      }
    } catch (error) {
      this.toastr.error("Error pausing the timer");
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async timerResume(): Promise<void> {
    this.isLoading = true;
    try {
      const response: HttpResponse<any> =
        await this.jobTimeTrackingService.pauseResumeTimer("resume");
      if (response.status === 200) {
        this.isPaused = false;
        this.startLocalTimer(this.secondsSignal());
      } else {
        console.log(response);
        this.toastr.error("Error resuming the timer");
      }
    } catch (error) {
      this.toastr.error("Error resuming the timer");
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async handleExistingTimeTrackingInfo() {
    this.isLoading = true;
    try {
      const status = await this.dashboardService.timeTrackingStatus();
      if (status && status.time_tracking_status) {
        const trackingStatus = status.time_tracking_status;
        this.isPaused = trackingStatus.is_paused;
        const current_time = trackingStatus.current_time;
        this.job = trackingStatus.job;

        this.secondsSignal.set(current_time);
        this.currentTimeTracked =
          this.jobTimeTrackingService.timeTrackedFormatter(current_time);

        if (!this.isPaused) {
          this.startLocalTimer(current_time);
        }
      } else {
        this.secondsSignal.set(0);
        this.isPaused = false;
      }
    } catch (error) {
      this.toastr.error("Error fetching time tracking info");
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async handleNewTimeTrackingInfo() {
    this.isLoading = true;
    try {
      const response = await this.jobTimeTrackingService.startTimeTracking(
        this.job.uuid,
        {
          is_billable: false,
          type: "client_work",
          notes: null,
        },
      );

      if (response && response.status === 201) {
        this.isPaused = false;
        const initial_time =
          this.jobTimeTrackingService.previousTrackingTimeTracked || 0;
        this.secondsSignal.set(initial_time);
        this.currentTimeTracked =
          this.jobTimeTrackingService.timeTrackedFormatter(initial_time);
        this.startLocalTimer(initial_time);
      } else {
        this.toastr.error("Error starting time tracking");
      }
    } catch (error) {
      this.toastr.error("Error starting time tracking");
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  subscribeToStartWithPreviousValue(): void {
    if (!this.startWithPreviousValueSubscription) {
      this.startWithPreviousValueSubscription =
        this.jobTimeTrackingService.startWithPreviousValue.subscribe(
          (res: any) => {
            this.startedWithPreviousTimeTracked = res;
          },
        );
    }
  }

  ngOnDestroy(): void {
    if (this.startWithPreviousValueSubscription) {
      this.startWithPreviousValueSubscription.unsubscribe();
    }
  }
}
